import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { SuccessSummaryItem } from '@/Pages/Success/components/SuccessSummaryItem';
import { successCopy } from '@/Pages/Success/copy';
import type { UserProfileState } from '@/Pages/Success/hooks/useUserProfile';
import { formatProfileDate } from '@/Pages/Success/utils/formatProfileDate';
import '@/Pages/Success/SuccessPage.sass';

type SuccessSummaryCardProps = {
  state: UserProfileState;
};

function SuccessSummaryCardSkeleton(): JSX.Element {
  return (
    <Box
      className="success-summary-card__skeleton"
      data-testid="success-summary-card-skeleton"
      aria-hidden="true"
    >
      <Skeleton variant="text" width="45%" height={24} />
      <Skeleton variant="text" width="80%" height={20} />
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="text" width="75%" height={20} />
      <Skeleton variant="text" width="60%" height={20} />
    </Box>
  );
}

function SuccessSummaryCardContent({
  state,
}: {
  state: Extract<UserProfileState, { status: 'success' }>;
}): JSX.Element {
  const { profile } = state;

  // Campo vazio não vira linha: o comprovante do convidado de evento não tem
  // "plano", e a data só aparece quando o cadastro da biometria tem data.
  const items = [
    { label: successCopy.summaryLabels.name, value: profile.name },
    { label: successCopy.summaryLabels.email, value: profile.email },
    { label: successCopy.summaryLabels.plan, value: profile.plan },
    {
      label: successCopy.summaryLabels.createdAt,
      value: profile.createdAt === '' ? '' : formatProfileDate(profile.createdAt),
    },
  ].filter((item) => item.value !== '');

  return (
    <Box
      component="ul"
      className="success-summary-card__list"
      data-testid="success-summary-card-list"
    >
      {items.map((item) => (
        <SuccessSummaryItem key={item.label} label={item.label} value={item.value} />
      ))}
    </Box>
  );
}

export function SuccessSummaryCard({ state }: SuccessSummaryCardProps): JSX.Element {
  const isLoading = state.status === 'loading';

  return (
    <Box
      component="section"
      className="success-summary-card"
      data-testid="success-summary-card"
      aria-busy={isLoading}
      aria-labelledby="success-summary-card-title"
    >
      <Typography
        component="h2"
        id="success-summary-card-title"
        className="success-summary-card__title"
      >
        {successCopy.summaryTitle}
      </Typography>

      {state.status === 'loading' ? <SuccessSummaryCardSkeleton /> : null}

      {state.status === 'success' ? <SuccessSummaryCardContent state={state} /> : null}

      {state.status === 'error' ? (
        <Typography
          component="p"
          role="alert"
          className="success-summary-card__error"
          data-testid="success-summary-card-error"
        >
          {state.message}
        </Typography>
      ) : null}
    </Box>
  );
}
